/**
 * Timing policy for one Truecaller sign-in (the callable + signing in with its
 * custom token). Pure, with injectable timers, so it is unit-tested.
 *
 * Why this exists: the sign-in often takes longer than the old fixed 10 s
 * stopwatch. When that fired it (a) showed "Truecaller took too long" even though
 * the rider was then signed in anyway, and (b) if the server's answer came back
 * AFTER the stopwatch, the app threw the login key away and sent the rider to a
 * paid SMS OTP although the server had already verified them.
 *
 * The rules now:
 *  - at HINT_MS the screen only says it is still working (nothing fails);
 *  - at TIMEOUT_MS, if still waiting and NOT already signed in, the rider is told
 *    to use OTP (the only moment the "took too long" message can appear);
 *  - a login key that comes back late is still used: the outcome is reported with
 *    `gaveUp: true` and the caller signs the rider in anyway;
 *  - an error that arrives after the rider was already told to use OTP is not
 *    reported a second time.
 */
export const TRUECALLER_HINT_MS = 10 * 1000;
export const TRUECALLER_TIMEOUT_MS = 30 * 1000;

const realTimers = { set: (fn, ms) => setTimeout(fn, ms), clear: (id) => clearTimeout(id) };

/**
 * @param {object}   opts
 * @param {Function} opts.verify      () => Promise<result> — the callable + sign-in
 * @param {Function} opts.isSignedIn  () => boolean — true once Firebase has a user
 * @param {Function} [opts.onHint]    called once at hintMs while still waiting
 * @param {Function} [opts.onGiveUp]  called once at timeoutMs while still waiting and not signed in
 * @returns {{ promise: Promise<{kind:'success'|'error', result?:any, error?:any, gaveUp:boolean}>, cancel: Function }}
 */
export const runTruecallerSignIn = ({
  verify,
  isSignedIn,
  onHint,
  onGiveUp,
  hintMs = TRUECALLER_HINT_MS,
  timeoutMs = TRUECALLER_TIMEOUT_MS,
  timers = realTimers,
}) => {
  let finished = false;
  let gaveUp = false;
  let hintTimer = null;
  let giveUpTimer = null;

  const clearTimers = () => {
    if (hintTimer != null) timers.clear(hintTimer);
    if (giveUpTimer != null) timers.clear(giveUpTimer);
    hintTimer = null;
    giveUpTimer = null;
  };

  const signedIn = () => {
    try { return Boolean(isSignedIn()); } catch { return false; }
  };

  hintTimer = timers.set(() => {
    if (finished || gaveUp || signedIn()) return;
    onHint?.();
  }, hintMs);

  giveUpTimer = timers.set(() => {
    if (finished || gaveUp) return;
    // Already signed in (the success path is about to run): nothing to tell the rider.
    if (signedIn()) return;
    gaveUp = true;
    onGiveUp?.();
  }, timeoutMs);

  const promise = (async () => {
    try {
      const result = await verify();
      return { kind: 'success', result, gaveUp };
    } catch (error) {
      return { kind: 'error', error, gaveUp };
    } finally {
      finished = true;
      clearTimers();
    }
  })();

  return {
    promise,
    // For when the screen goes away: stop the timers, never fire a callback again.
    cancel: () => {
      finished = true;
      clearTimers();
    },
  };
};
